import { registerPlugin } from '@capacitor/core';
import { authFetch } from './authFetch.js';
import { hasPlayBilling } from './purchase.js';

// Google Play billing for Pro inside the Android app (native side:
// zephyr-android .../billing/ZephyrBillingPlugin.kt). The app never grants
// Pro itself: every purchase token goes to lambda/play-billing, which checks
// it with Google and turns Pro on or off.
const ZephyrBilling = registerPlugin('ZephyrBilling');
const PRODUCT_ID = 'zephyr_pro_monthly';
const API_URL = import.meta.env.VITE_BACKEND_URL || '';

async function verify(purchaseToken) {
    const response = await authFetch(`${API_URL}/api/play/verify`, {
        method: 'POST',
        body: JSON.stringify({ purchaseToken })
    });
    if (!response.ok) throw new Error(`Verify failed: ${response.status}`);
    return response.json(); // { entitled, state, expiryTime }
}

export const playBillingService = {
    available: hasPlayBilling,

    // The local price for the Pro button, e.g. "A$9.99" (null if unavailable).
    async getPrice() {
        if (!hasPlayBilling()) return null;
        try {
            const product = await ZephyrBilling.getProduct({ productId: PRODUCT_ID });
            return product.available ? product.formattedPrice : null;
        } catch (e) {
            console.warn('Play product lookup failed:', e?.message || e);
            return null;
        }
    },

    // Opens Google Play's purchase sheet. Resolves true once Pro is on.
    async purchase(userId) {
        const result = await ZephyrBilling.purchase({ productId: PRODUCT_ID, accountId: userId });
        if (result.status === 'cancelled') return false;
        if (result.status === 'owned') return (await this.syncPurchases()) === true;
        if (result.status === 'pending') {
            alert('Your purchase is pending with Google Play. Pro turns on once the payment completes.');
            await verify(result.purchaseToken).catch(() => {});
            return false;
        }
        const verified = await verify(result.purchaseToken);
        return !!verified.entitled;
    },

    // Re-sends this phone's Play purchases so renewals (and restores after a
    // reinstall) keep Pro on. Returns true if any purchase grants Pro, false
    // if none does, null if there was nothing to check.
    async syncPurchases() {
        if (!hasPlayBilling()) return null;
        const { purchases = [] } = await ZephyrBilling.getActivePurchases();
        const ours = purchases.filter(p => p.productId === PRODUCT_ID && p.purchaseToken);
        if (ours.length === 0) return null;
        let entitled = false;
        for (const p of ours) {
            try {
                if ((await verify(p.purchaseToken)).entitled) entitled = true;
            } catch (e) {
                console.warn('Play purchase sync failed:', e?.message || e);
            }
        }
        return entitled;
    }
};
